import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { Profile } from '../types';

interface SubjectsPageProps {
  showToast: (message: string, type?: 'success' | 'error') => void;
}

export const SubjectsPage: React.FC<SubjectsPageProps> = ({ showToast }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [matieres, setMatieres] = useState('');

  // Suggestions de matières par filière (source unifiée avec EditProfilePage)
  const suggestionsByKey: Record<'IG' | 'EE' | 'IBM' | 'IS', string[]> = {
    IBM: [
      'General Management',
      'Accounting & Finance',
      'Marketing & Communication',
      'Leadership & Soft-skills',
      'Economics',
      'Business Law',
      'Business Tools',
      'Communication professionnelle',
      'Langues',
      'Électifs',
      'Bachelor Thesis'
    ],
    IG: [
      'Idéation & Innovation',
      'Business Analyse & Modélisation',
      'Gestion des données',
      'Programmation (fondamentale → collaborative)',
      'Infrastructure / Réseaux / Sécurité',
      'Entreprise connectée',
      'Leadership & Communication de projet',
      'Web & Internet Programming',
      'Architecture & Services',
      'Math for Machine Learning',
      'Wikinomie & Économie numérique',
      'Projets sur mandat',
      "Stratégie & Gestion d’entreprise",
      'Intégration & Cycle de développement',
      'Automatisation des processus métiers',
      'Technologies émergentes',
      'Industrialisation des services',
      'Valorisation des données',
      'Outils méthodologiques',
      'Travail de bachelor'
    ],
    IS: [
      'Bibliothéconomie & Gestion des collections',
      'Archivistique & Records Management',
      'Catalogage, Métadonnées & Gestion des données',
      'Technologies Web & Développement',
      'Sécurité & Systèmes d’information',
      'Recherche d’information & Veille stratégique',
      'Société de l’information, Médiation & Open Access',
      'Gestion de projet & Leadership',
      'Stratégie des services d’information',
      'UX & Ergonomie numérique',
      'Technologies émergentes & Big Data',
      'Stages professionnels',
      'Travail de bachelor'
    ],
    EE: [
      'Management général & stratégique',
      'Comptabilité financière & Controlling',
      'Corporate Finance & Banking',
      'Marketing & Communication',
      'Ressources Humaines',
      'Economics & Society',
      'Law & Taxation',
      'Quantitative Methods & Data Science',
      'Digital & Information Skills',
      'Soft-skills & Leadership durable',
      'Entrepreneurship & Innovation',
      'Électifs / Spécialisations',
      'Bachelor Thesis'
    ]
  };

  const getFiliereKey = (f?: string): 'IG' | 'EE' | 'IBM' | 'IS' | undefined => {
    if (!f) return undefined;
    const s = f.toLowerCase();
    if (s.includes('informatique de gestion') || s === 'ig' || s.includes(' ig')) return 'IG';
    if (s.includes("economie d'entreprise") || s.includes('économie d\'entreprise') || s === 'ee' || s.includes(' ee')) return 'EE';
    if (s.includes('international business') || s === 'ibm' || s.includes(' ibm')) return 'IBM';
    if (s.includes('information science') || s === 'is' || s.includes(' is')) return 'IS';
    return undefined;
  };

  const loadProfile = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await api.profiles.getByUserId(user!.id);
      
      if (error) {
        showToast('Erreur lors du chargement du profil', 'error');
        navigate('/profile');
        return;
      }

      if (data) {
        setProfile(data);
        setMatieres(data.matieres || '');
      }
    } catch (err) {
      showToast('Une erreur inattendue s\'est produite', 'error');
      navigate('/profile');
    } finally {
      setLoading(false);
    }
  }, [user, navigate, showToast]);

  useEffect(() => {
    if (user) {
      loadProfile();
    }
  }, [user, loadProfile]);

  // Vérifier que l'utilisateur a le droit d'accéder à cette page
  useEffect(() => {
    if (profile && profile.role === 'etudiant') {
      showToast('Cette page est réservée aux tuteur-trices', 'error');
      navigate('/profile');
    }
  }, [profile, navigate, showToast]); // eslint-disable-line react-hooks/exhaustive-deps


  const addSuggestion = (matiere: string) => {
    const currentMatieres = matieres.split(',').map(m => m.trim()).filter(m => m);
    if (!currentMatieres.includes(matiere)) {
      const newMatieres = [...currentMatieres, matiere].join(', ');
      setMatieres(newMatieres);
    }
  };

  const removeMatiereByIndex = (index: number) => {
    const currentMatieres = matieres.split(',').map(m => m.trim()).filter(m => m);
    currentMatieres.splice(index, 1);
    setMatieres(currentMatieres.join(', '));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const hp = (document.getElementById('hp_field') as HTMLInputElement | null)?.value;
    if (hp) {
      return;
    }
    
    if (!profile || !matieres.trim()) {
      showToast('Veuillez saisir au moins une matière', 'error');
      return;
    }

    setSaving(true);

    try {
      const { error } = await api.profiles.update(profile.id, {
        matieres: matieres.trim()
      });

      if (error) {
        showToast('Erreur lors de la mise à jour des matières', 'error');
        return;
      }

      showToast('Matières mises à jour avec succès !', 'success');
      
      // Scroll automatiquement en haut de la page
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Rester dans la page des matières au lieu de rediriger
    } catch (err) {
      showToast('Une erreur inattendue s\'est produite', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#002c46] mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  const currentMatieres = matieres.split(',').map(m => m.trim()).filter(m => m);
  const suggestionsKey = getFiliereKey(profile?.filiere);
  const suggestions = suggestionsKey ? suggestionsByKey[suggestionsKey] : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto h-16 w-16 bg-purple-600 rounded-full flex items-center justify-center mb-4">
            <i className="fas fa-book text-white text-2xl"></i>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Matières enseignées
          </h1>
          <p className="text-gray-600">
            Gérez les matières que vous proposez d'enseigner
          </p>
        </div>

        {/* Formulaire */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Champ honeypot anti-spam (caché) */}
            <input id="hp_field" name="homepage" type="text" className="hidden" autoComplete="off" tabIndex={-1} aria-hidden="true" />
            {/* Matières actuelles */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Vos matières (sélection depuis la liste de votre filière) *
              </label>
              <textarea
                value={matieres}
                readOnly
                disabled
                rows={3}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-600"
                placeholder={suggestions.length === 0 ? 'Aucune suggestion disponible pour votre filière' : 'Ajoutez vos matières via la liste de suggestions ci-dessous'}
              />
              <p className="text-xs text-gray-500 mt-1">Le champ est verrouillé. Utilisez les suggestions ci-dessous.</p>
            </div>

            {/* Matières ajoutées */}
            {currentMatieres.length > 0 && (
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Matières ajoutées :</h3>
                <div className="flex flex-wrap gap-2">
                  {currentMatieres.map((matiere, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800"
                    >
                      {matiere}
                      <button
                        type="button"
                        onClick={() => removeMatiereByIndex(index)}
                        className="ml-2 text-purple-600 hover:text-purple-800"
                      >
                        <i className="fas fa-times text-xs"></i>
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Suggestions */}
            {suggestions.length > 0 && (
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">
                  <i className="fas fa-lightbulb text-yellow-500 mr-2"></i>
                  Suggestions pour {profile?.filiere} :
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => addSuggestion(suggestion)}
                      className="text-left px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-purple-50 hover:border-purple-300 transition-colors"
                      disabled={currentMatieres.includes(suggestion)}
                    >
                      <i className="fas fa-plus text-purple-600 mr-2"></i>
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}


            {/* Boutons */}
            <div className="flex gap-4 pt-6">
              <button
                type="button"
                onClick={() => navigate('/profile')}
                className="flex-1 py-3 px-6 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                <i className="fas fa-arrow-left mr-2"></i>
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 py-3 px-6 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Sauvegarde...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save mr-2"></i>
                    Sauvegarder
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}; 